import { supabase } from '../lib/supabase';

function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
}

const returnInspectionService = {
  async createInspection(bookingId, renterId, ownerId, trailerListingId) {
    try {
      const { data, error } = await supabase
        ?.from('return_inspections')
        ?.insert({
          booking_id: bookingId,
          renter_id: renterId,
          owner_id: ownerId,
          trailer_listing_id: trailerListingId,
          status: 'in_progress',
          started_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) {
        console.error('Create inspection error:', error?.message);
        return { data: null, error };
      }
      return { data, error: null };
    } catch (error) {
      console.error('Create inspection error:', error);
      return { data: null, error: { message: error?.message } };
    }
  },

  async getInspectionByBookingId(bookingId) {
    try {
      const { data, error } = await supabase
        ?.from('return_inspections')
        ?.select('*')
        ?.eq('booking_id', bookingId)
        ?.maybeSingle();

      if (error) {
        if (error?.code === 'PGRST116') return { data: null, error: null };
        return { data: null, error };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async uploadPhoto(file, inspectionId, section) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'Not authenticated' } };

      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${inspectionId}/${section}/${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;

      const { data, error } = await supabase?.storage
        ?.from('return-inspection-photos')
        ?.upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (error) {
        console.error('Upload photo error:', error?.message);
        return { data: null, error };
      }
      return { data: { path: filePath }, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async getPhotoUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('return-inspection-photos')
        ?.createSignedUrl(filePath, 3600);

      if (error) return { data: null, error };
      return { data: data?.signedUrl, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async updateSectionPhotos(inspectionId, section, photoPaths) {
    try {
      const columnMap = {
        exterior: 'exterior_photos',
        interior: 'interior_photos',
        hitch: 'hitch_photos',
        electrical: 'electrical_photos',
        tires: 'tire_photos',
        damage: 'damage_photos'
      };
      const columnName = columnMap?.[section];
      if (!columnName) return { data: null, error: { message: 'Invalid section' } };

      const { data, error } = await supabase
        ?.from('return_inspections')
        ?.update({ [columnName]: photoPaths })
        ?.eq('id', inspectionId)
        ?.select()
        ?.single();

      if (error) return { data: null, error };
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async updateSectionNotes(inspectionId, section, notes) {
    try {
      const { data: existing } = await supabase
        ?.from('return_inspections')
        ?.select('section_notes')
        ?.eq('id', inspectionId)
        ?.single();

      const currentNotes = existing?.section_notes || {};
      const updatedNotes = { ...currentNotes, [section]: notes };

      const { data, error } = await supabase
        ?.from('return_inspections')
        ?.update({ section_notes: updatedNotes })
        ?.eq('id', inspectionId)
        ?.select()
        ?.single();

      if (error) return { data: null, error };
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async updateDamageReport(inspectionId, damageReports) {
    try {
      const { data, error } = await supabase
        ?.from('return_inspections')
        ?.update({ damage_report: damageReports })
        ?.eq('id', inspectionId)
        ?.select()
        ?.single();

      if (error) return { data: null, error };
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async updateGpsLocation(inspectionId, latitude, longitude) {
    try {
      const { data, error } = await supabase
        ?.from('return_inspections')
        ?.update({
          gps_latitude: latitude,
          gps_longitude: longitude,
          gps_timestamp: new Date()?.toISOString()
        })
        ?.eq('id', inspectionId)
        ?.select()
        ?.single();

      if (error) return { data: null, error };
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  generateInspectionReport(inspectionData, sections, damageReports, gpsLocation) {
    const completedSections = sections?.filter(s =>
      inspectionData?.[s?.id]?.photos?.length >= s?.requiredPhotos
    );
    const totalPhotos = sections?.reduce(
      (sum, s) => sum + (inspectionData?.[s?.id]?.photos?.length || 0), 0
    );

    return {
      generatedAt: new Date()?.toISOString(),
      reportId: `RI-${Date.now()}`,
      summary: {
        totalSections: sections?.length,
        completedSections: completedSections?.length,
        totalPhotos,
        damageReportsCount: damageReports?.length,
        hasDamage: damageReports?.length > 0
      },
      gpsVerification: gpsLocation
        ? {
            verified: true,
            latitude: gpsLocation?.latitude,
            longitude: gpsLocation?.longitude,
            timestamp: gpsLocation?.timestamp || new Date()?.toISOString()
          }
        : { verified: false },
      sections: sections?.map(section => ({
        id: section?.id,
        title: section?.title,
        photosCount: inspectionData?.[section?.id]?.photos?.length || 0,
        requiredPhotos: section?.requiredPhotos,
        complete: (inspectionData?.[section?.id]?.photos?.length || 0) >= section?.requiredPhotos,
        notes: inspectionData?.[section?.id]?.notes || ''
      })),
      damageReports: damageReports?.map(r => ({
        location: r?.location,
        severity: r?.severity,
        description: r?.description,
        timestamp: r?.timestamp
      })),
      overallCondition: damageReports?.length === 0
        ? 'Good'
        : damageReports?.some(r => r?.severity === 'major')
        ? 'Poor'
        : damageReports?.some(r => r?.severity === 'moderate')
        ? 'Fair' :'Minor Issues'
    };
  },

  async submitInspection(inspectionId, inspectionReport, damageReports) {
    try {
      const { data, error } = await supabase
        ?.from('return_inspections')
        ?.update({
          status: 'completed',
          completed_at: new Date()?.toISOString(),
          inspection_report: inspectionReport,
          damage_report: damageReports,
          damage_severity: damageReports?.length === 0
            ? 'none'
            : damageReports?.some(r => r?.severity === 'major')
            ? 'severe'
            : damageReports?.some(r => r?.severity === 'moderate')
            ? 'moderate' :'minor'
        })
        ?.eq('id', inspectionId)
        ?.select()
        ?.single();

      if (error) return { data: null, error };
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async notifyOwner(inspectionId, ownerId, renterId, bookingId) {
    try {
      const { data, error } = await supabase
        ?.from('messages')
        ?.insert({
          booking_id: bookingId,
          sender_id: renterId,
          receiver_id: ownerId,
          content: `Renter has completed the trailer return inspection. Inspection ID: ${inspectionId}. Please review the documentation.`
        })
        ?.select()
        ?.single();

      if (error) {
        console.error('Notify owner error:', error?.message);
        return { data: null, error };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  }
};

export default returnInspectionService;
